import { useEffect, useState } from "react";
import { useParams } from "wouter";

// ── Types ──────────────────────────────────────────────────────────────────
interface Branch {
  id: string;
  branchName: string;
  address: string | null;
  city: string | null;
  countryCode: string | null;
  phone: string | null;
  email: string | null;
  lat: number | null;
  lng: number | null;
  hours: Record<string, string> | null;
  branchLinks: { label: string; url: string }[];
  distanceKm?: number;
}

interface ScanProfile {
  username: string;
  displayName: string;
  bio: string | null;
  phone: string | null;
  email: string | null;
  avatarUrl: string | null;
  location: string | null;
  website: string | null;
  theme: string;
}

interface ScanResult {
  authenticated: boolean;
  firstScan: boolean;
  orgName: string;
  logoUrl: string | null;
  orgSlug: string;
  serialCode: string;
  activatedAt: string;
  noProfile: boolean;
  profile?: ScanProfile;
  nearestBranch?: Branch | null;
  branches?: Branch[];
}

type Step = "loading" | "verified" | "result" | "invalid";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api-server/api/v1";

export default function DualSecretScan() {
  const params = useParams<{ orgSlug: string; serialCode: string }>();
  const { orgSlug, serialCode } = params;

  const [step, setStep]       = useState<Step>("loading");
  const [data, setData]       = useState<ScanResult | null>(null);
  const [errMsg, setErrMsg]   = useState<string>("");

  useEffect(() => {
    if (!orgSlug || !serialCode) return;

    // Try to get browser GPS first, then fall back to IP geo on the server
    const doScan = async (coords?: { lat: number; lng: number }) => {
      try {
        const qs = coords ? `?lat=${coords.lat}&lng=${coords.lng}` : "";
        const res = await fetch(`${API_BASE}/scan/${encodeURIComponent(orgSlug)}/${encodeURIComponent(serialCode)}${qs}`);
        const json = await res.json();

        if (!res.ok || !json.authenticated) {
          setErrMsg(json.message ?? "This product could not be verified.");
          setStep("invalid");
          return;
        }

        setData(json as ScanResult);

        // Brief pause on "Verifying..." then animate to "Verified" checkmark
        setTimeout(() => {
          setStep("verified");
          setTimeout(() => setStep("result"), 1800);
        }, 1200);
      } catch {
        setErrMsg("Network error — please check your connection and try again.");
        setStep("invalid");
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => doScan({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        ()    => doScan(), // user denied GPS — server will use IP geo
        { timeout: 3000 },
      );
    } else {
      doScan();
    }
  }, [orgSlug, serialCode]);

  // ── Step 1: Loading ────────────────────────────────────────────────────
  if (step === "loading") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-950 text-white px-6">
        <div className="flex flex-col items-center gap-6 animate-pulse">
          <div className="w-20 h-20 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
          <p className="text-xl font-medium text-gray-200 tracking-wide">Verifying product&hellip;</p>
          <p className="text-sm text-gray-500 font-mono">{serialCode}</p>
        </div>
      </div>
    );
  }

  // ── Invalid / Not Found ────────────────────────────────────────────────
  if (step === "invalid") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-950 text-white px-6">
        <div className="flex flex-col items-center gap-6 max-w-sm text-center">
          <div className="w-20 h-20 rounded-full bg-red-500/10 border-2 border-red-500 flex items-center justify-center">
            <span className="text-4xl">✕</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-red-400 mb-2">Verification Failed</h1>
            <p className="text-gray-400">{errMsg}</p>
          </div>
          <div className="mt-2 px-4 py-3 rounded-lg bg-red-900/20 border border-red-800 text-sm text-red-300 w-full">
            If you believe this product is genuine, contact the brand directly for support.
          </div>
          <p className="text-xs text-gray-600 font-mono mt-2">
            CommonQR Authentication &nbsp;·&nbsp; {orgSlug}/{serialCode}
          </p>
        </div>
      </div>
    );
  }

  // ── Step 2: Authenticated — show green checkmark ─────────────────────
  if (step === "verified") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-950 text-white px-6">
        <div className="flex flex-col items-center gap-6 animate-in fade-in zoom-in duration-500">
          {/* Animated green checkmark */}
          <div className="relative w-24 h-24">
            <div className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
            <div className="relative w-24 h-24 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/40">
              <svg className="w-12 h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-emerald-400">Authentic Product Verified</h1>
            {data?.firstScan && (
              <p className="text-sm text-emerald-300/70 mt-1">First scan — this product is newly activated</p>
            )}
          </div>
          <p className="text-xs text-gray-600 font-mono">{data?.orgName} · {serialCode}</p>
        </div>
      </div>
    );
  }

  // ── Step 3: Result ─────────────────────────────────────────────────────
  if (!data) return null;

  const branch = data.nearestBranch;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header verified strip */}
      <div className="bg-emerald-600 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        Authentic Product · CommonQR Authentication
      </div>

      <div className="max-w-lg mx-auto px-4 py-8 flex flex-col gap-6">

        {/* Org / Brand header */}
        <div className="flex items-center gap-4">
          {data.logoUrl ? (
            <img src={data.logoUrl} alt={data.orgName} className="w-14 h-14 rounded-xl object-contain bg-white/5 p-1" />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-2xl font-bold">
              {(data.orgName ?? "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-0.5">Verified by</p>
            <h2 className="text-xl font-bold text-white">{data.orgName}</h2>
            {data.firstScan && (
              <span className="inline-block text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-700 rounded-full px-2 py-0.5 mt-1">
                ✦ First Scan
              </span>
            )}
          </div>
        </div>

        {/* Token info */}
        <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3 flex flex-col gap-1">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Token ID</span>
            <span className="font-mono text-gray-300 text-xs">{data.serialCode}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">First activated</span>
            <span className="text-gray-300 text-xs">
              {new Date(data.activatedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          </div>
        </div>

        {/* No profile case — just show basic verified info */}
        {data.noProfile && (
          <div className="rounded-xl bg-white/5 border border-white/10 px-6 py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 border-2 border-emerald-600 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-1">Verified by {data.orgName}</h3>
            <p className="text-gray-400 text-sm">This product is genuine and authenticated by CommonQR product authentication.</p>
          </div>
        )}

        {/* Profile + nearest branch */}
        {!data.noProfile && data.profile && (
          <>
            {/* Brand profile card */}
            <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
              <div className="bg-gradient-to-r from-teal-900/40 to-emerald-900/40 px-5 py-4 flex items-center gap-4">
                {data.profile.avatarUrl ? (
                  <img src={data.profile.avatarUrl} alt={data.profile.displayName} className="w-12 h-12 rounded-full object-cover ring-2 ring-emerald-500/40" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-lg font-bold">
                    {data.profile.displayName?.charAt(0) ?? "?"}
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-white">{data.profile.displayName}</h3>
                  {data.profile.bio && <p className="text-sm text-gray-300 line-clamp-2">{data.profile.bio}</p>}
                </div>
              </div>
              <div className="px-5 py-3 flex flex-col gap-2 text-sm">
                {data.profile.phone && (
                  <a href={`tel:${data.profile.phone}`} className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
                    <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {data.profile.phone}
                  </a>
                )}
                {data.profile.website && (
                  <a href={data.profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors">
                    <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    {data.profile.website.replace(/^https?:\/\//, "")}
                  </a>
                )}
              </div>
            </div>

            {/* Nearest branch */}
            {branch && (
              <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-300 flex items-center gap-1.5">
                    <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Nearest Branch
                  </span>
                  {branch.distanceKm !== undefined && (
                    <span className="text-xs bg-emerald-500/15 text-emerald-300 border border-emerald-700/50 rounded-full px-2 py-0.5">
                      {branch.distanceKm} km away
                    </span>
                  )}
                </div>
                <div className="px-4 py-4 flex flex-col gap-3">
                  <h4 className="font-semibold text-white">{branch.branchName}</h4>
                  {(branch.address || branch.city) && (
                    <p className="text-sm text-gray-400">
                      {[branch.address, branch.city, branch.countryCode].filter(Boolean).join(", ")}
                    </p>
                  )}
                  {branch.phone && (
                    <a href={`tel:${branch.phone}`} className="inline-flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      {branch.phone}
                    </a>
                  )}

                  {/* Branch hours */}
                  {branch.hours && Object.keys(branch.hours).length > 0 && (
                    <details className="group">
                      <summary className="text-sm text-gray-400 cursor-pointer hover:text-gray-300 flex items-center gap-1.5 list-none">
                        <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Opening hours</span>
                        <svg className="w-3 h-3 ml-auto transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </summary>
                      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-400">
                        {Object.entries(branch.hours).map(([day, hrs]) => (
                          <div key={day} className="flex justify-between">
                            <span className="text-gray-500">{day}</span>
                            <span>{hrs}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {/* Branch links */}
                  {branch.branchLinks?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {branch.branchLinks.map((link, i) => (
                        <a
                          key={i}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-3 py-1 rounded-full border border-emerald-700/60 text-emerald-300 hover:bg-emerald-500/10 transition-colors"
                        >
                          {link.label}
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Map link */}
                  {branch.lat && branch.lng && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${branch.lat},${branch.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      Get Directions
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Fallback: all branches list when no nearest detected */}
            {!branch && data.branches && data.branches.length > 0 && (
              <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
                <div className="px-4 py-3 border-b border-white/10">
                  <span className="text-sm font-medium text-gray-300">All Branches</span>
                </div>
                <div className="divide-y divide-white/5">
                  {data.branches.map((b) => (
                    <div key={b.id} className="px-4 py-3">
                      <p className="text-sm font-medium text-white">{b.branchName}</p>
                      {(b.address || b.city) && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {[b.address, b.city].filter(Boolean).join(", ")}
                        </p>
                      )}
                      {b.phone && (
                        <a href={`tel:${b.phone}`} className="text-xs text-emerald-400 hover:underline mt-0.5 block">{b.phone}</a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="text-center mt-2">
          <a href="/" className="text-xs text-gray-600 hover:text-gray-500 transition-colors">
            Powered by <span className="text-emerald-600 font-semibold">CommonQR</span> Authentication
          </a>
        </div>

      </div>
    </div>
  );
}
